import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Feature,
  useGetFeatureToggles,
  useInvalidateUseGetFeatureToggles,
} from '@urbanx/agx.agencies.client';
import {
  type CampaignDetailModel,
  InProgressFormModelFormType,
  useGetCampaign,
  useInvalidateUseGetCampaign,
} from '@urbanx/agx.campaigns.client';
import {
  type ProposalResponse,
  ProposalResponseStatus,
  ProposalResponseProposalType,
  useListProposals,
  useInvalidateUseListProposals,
} from '@urbanx/agx.proposals.client';
import { AgxButton, useDialog, getAustralianStateTimeZone } from '@urbanx/agx.ui-components2';
import { useLocation, useNavigate } from 'react-router-dom';
import { AgreementReadinessDialog } from '../readiness/AgreementReadinessDialog';
import { useOpenAgreement } from '../readiness/AgreementConversion';
import * as styles from './ProposalDashboardPanel.css';

interface ProposalDashboardPanelProps {
  campaign: CampaignDetailModel;
}

export const ProposalDashboardPanel = ({ campaign }: ProposalDashboardPanelProps) => {
  const feature = useGetFeatureToggles();
  const invalidateFeature = useInvalidateUseGetFeatureToggles();
  if (feature.isPending) return <p role="status">Loading proposal access…</p>;
  if (feature.isError)
    return (
      <div role="alert">
        Unable to load proposal access.
        <AgxButton
          id="dashboard-access-retry"
          text="Retry access"
          variant="hollow"
          onClick={() => void invalidateFeature()}
        />
      </div>
    );
  if (!feature.data?.enabledFeatures?.includes(Feature.Proposals))
    return <p>Proposals are not enabled.</p>;
  if (!campaign.campaignId) return <p role="alert">This campaign has no saved identity.</p>;
  return <CampaignProposals campaign={campaign} campaignId={campaign.campaignId} />;
};

const CampaignProposals = ({
  campaign,
  campaignId,
}: {
  campaign: CampaignDetailModel;
  campaignId: string;
}) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [refreshed, setRefreshed] = useState<string>();
  const proposals = useListProposals(undefined, campaignId, refreshed === location.key);
  const invalidate = useInvalidateUseListProposals();
  const [selection, setSelection] = useState<{ campaignId: string; proposalId: string }>();
  useEffect(() => {
    let active = true;
    // A new visit must also stale inactive campaigns; switching within this visit must not.
    void queryClient
      .invalidateQueries({ queryKey: ['listProposals'], refetchType: 'none' })
      .then(() => {
        if (active) setRefreshed(location.key);
      });
    return () => {
      active = false;
    };
  }, [location.key, queryClient]);
  if (refreshed !== location.key || proposals.isPending || proposals.isFetching)
    return <p role="status">Loading proposals…</p>;
  if (proposals.isError)
    return (
      <div role="alert">
        Unable to load proposals.{' '}
        <AgxButton
          id="dashboard-proposals-retry"
          text="Retry proposals"
          variant="hollow"
          onClick={() => void invalidate(undefined, campaignId)}
        />
      </div>
    );
  const matching =
    proposals.data?.groups
      .flatMap((group) => group.proposals)
      .filter((proposal) => proposal.subject.campaignId === campaignId) ?? [];
  const selected =
    matching.length === 1
      ? matching[0]
      : matching.find(
          (proposal) =>
            selection?.campaignId === campaignId && proposal.proposalId === selection.proposalId
        );
  return (
    <section className={styles.panel} aria-label="Campaign proposals">
      <div className={styles.caption}>Proposal</div>
      <h2 className={styles.address}>
        {[
          campaign.address?.unitNumber
            ? `${campaign.address.unitNumber}/${campaign.address.streetAddress ?? ''}`
            : campaign.address?.streetAddress,
          campaign.address?.suburb,
        ]
          .filter(Boolean)
          .join(', ') || 'Campaign proposal'}
      </h2>
      {matching.length > 1 && (
        <label className={styles.chooser}>
          Choose a proposal
          <select
            aria-label="Choose a proposal"
            value={selected?.proposalId ?? ''}
            onChange={(event) => setSelection({ campaignId, proposalId: event.target.value })}
          >
            <option value="">Select a saved presentation</option>
            {matching.map((proposal) => (
              <option key={proposal.proposalId} value={proposal.proposalId}>
                {proposal.title} · {proposal.status} · {proposal.proposalId}
              </option>
            ))}
          </select>
        </label>
      )}
      {selected ? (
        <SavedProposal
          key={selected.proposalId}
          proposal={selected}
          campaign={campaign}
          campaignId={campaignId}
        />
      ) : matching.length === 0 ? (
        <>
          <div className={styles.card}>
            <h3>No saved presentation yet</h3>
            <p>Continue the native Listing Proposal draft to prepare your presentation.</p>
          </div>
          <NativeDraftLinks key={campaignId} campaignId={campaignId} />
          <AgxButton
            id="dashboard-proposals-refresh"
            text="Refresh proposals"
            variant="hollow"
            onClick={() => void invalidate(undefined, campaignId)}
          />
        </>
      ) : (
        <p>Select the presentation you want to reopen.</p>
      )}
    </section>
  );
};

const SavedProposal = ({
  proposal,
  campaign,
  campaignId,
}: {
  proposal: ProposalResponse;
  campaign: CampaignDetailModel;
  campaignId: string;
}) => {
  const navigate = useNavigate();
  const readiness = useDialog(AgreementReadinessDialog);
  const openAgreement = useOpenAgreement();
  const draft = proposal.status === ProposalResponseStatus.Draft;
  const listing = proposal.proposalType === ProposalResponseProposalType.Listing;
  const accepted = proposal.status === ProposalResponseStatus.Accepted;
  const status = proposal.status === ProposalResponseStatus.Shared ? 'Live' : proposal.status;
  const reviewable =
    listing &&
    [
      ProposalResponseStatus.Draft,
      ProposalResponseStatus.Shared,
      ProposalResponseStatus.Accepted,
    ].includes(proposal.status);
  const state = campaign.state ?? campaign.address?.state;
  const saved = state
    ? new Intl.DateTimeFormat('en-AU', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: getAustralianStateTimeZone(state),
      }).format(new Date(proposal.currentRevision.savedUtc))
    : `${proposal.currentRevision.savedUtc} (UTC)`;
  return (
    <>
      <h3 className={styles.title}>{proposal.title}</h3>
      <div className={styles.pills}>
        <span className={accepted ? styles.acceptedPill : styles.pill}>{status}</span>
        <span className={styles.pill}>{listing ? 'Listing proposal' : 'General proposal'}</span>
      </div>
      {accepted && (
        <div className={styles.congratulations}>
          <span className={styles.brandMark} aria-hidden="true">
            ●
          </span>
          <h3>Congratulations</h3>
          <p>
            {proposal.recipient.greetingName
              ? `${proposal.recipient.greetingName} accepted your proposal.`
              : 'Your proposal has been accepted.'}
          </p>
          {reviewable && (
            <>
              <p>
                Review the details before preparing an agreement. Nothing is created or sent until
                you confirm an agreement draft.
              </p>
              <AgxButton
                id="dashboard-review"
                text="Review agreement readiness"
                variant="primary"
                onClick={() =>
                  readiness.open({ campaignId, proposalId: proposal.proposalId, openAgreement })
                }
              />
            </>
          )}
        </div>
      )}
      <div className={styles.card}>
        <h3>Proposal summary</h3>
        <dl className={styles.summary}>
          <SummaryRow label="Status" value={status} />
          <SummaryRow
            label="Template"
            value={`${proposal.sourceTemplateId} · Version ${proposal.sourceTemplateVersion}`}
          />
          <SummaryRow
            label="Sections"
            value={`${proposal.currentRevision.sections.filter((section) => section.enabled !== false).length} included`}
          />
          <SummaryRow label="Saved revision" value={String(proposal.currentRevisionNumber)} />
          <SummaryRow label="Last saved" value={saved} />
          <SummaryRow label="Recipient" value={proposal.recipient.greetingName ?? 'Not recorded'} />
        </dl>
      </div>
      {proposal.status === ProposalResponseStatus.Shared && (
        <p className={styles.caption}>
          Live means the saved proposal is Shared. Link expiry, engagement metrics and activity are
          not available.
        </p>
      )}
      <div className={styles.actions}>
        <AgxButton
          id="dashboard-preview"
          text="Preview"
          variant="hollow"
          onClick={() =>
            navigate(`/proposals/${encodeURIComponent(proposal.proposalId)}?preview=1`)
          }
        />
        {draft && (
          <AgxButton
            id="dashboard-continue"
            text="Continue proposal"
            variant="primary"
            onClick={() => navigate(`/proposals/${encodeURIComponent(proposal.proposalId)}`)}
          />
        )}
        {reviewable && !accepted && (
          <AgxButton
            id="dashboard-review"
            text="Review agreement readiness"
            variant="hollow"
            onClick={() =>
              readiness.open({ campaignId, proposalId: proposal.proposalId, openAgreement })
            }
          />
        )}
      </div>
      {draft && <NativeDraftLinks campaignId={campaignId} proposalId={proposal.proposalId} />}
      {!draft && <p className={styles.caption}>This saved presentation is read-only.</p>}
    </>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.row}>
    <dt>{label}</dt>
    <dd className={styles.value}>{value}</dd>
  </div>
);

const NativeDraftLinks = ({
  campaignId,
  proposalId,
}: {
  campaignId: string;
  proposalId?: string;
}) => {
  const campaign = useGetCampaign(campaignId);
  const invalidate = useInvalidateUseGetCampaign();
  const invalidateRef = useRef(invalidate);
  useEffect(() => {
    invalidateRef.current = invalidate;
  }, [invalidate]);
  const [ready, setReady] = useState(false);
  const [formId, setFormId] = useState('');
  const navigate = useNavigate();
  useEffect(() => {
    let active = true;
    void invalidateRef.current(campaignId).then(() => {
      if (active) setReady(true);
    });
    return () => {
      active = false;
    };
  }, [campaignId]);
  if (!ready || campaign.isPending || campaign.isFetching)
    return <p role="status">Loading native proposal drafts…</p>;
  if (campaign.isError)
    return (
      <div role="alert">
        Unable to load proposal drafts.
        <AgxButton
          id="dashboard-drafts-retry"
          text="Retry drafts"
          variant="hollow"
          onClick={() => void invalidate(campaignId)}
        />
      </div>
    );
  const forms =
    campaign.data?.inProgressForms?.filter(
      (form) => form.formType === InProgressFormModelFormType.ListingProposal && form.formId
    ) ?? [];
  const selected = forms.length === 1 ? forms[0] : forms.find((form) => form.formId === formId);
  if (!forms.length)
    return (
      <div>
        <p>
          No in-progress Listing Proposal form is available for this campaign. Return to Campaigns
          to start a new proposal, or refresh after restoring the draft.
        </p>
        <AgxButton
          id="dashboard-drafts-refresh"
          text="Refresh drafts"
          variant="hollow"
          onClick={() => void invalidate(campaignId)}
        />
      </div>
    );
  const open = (setup: boolean) => {
    if (!selected?.formId) return;
    const query = new URLSearchParams({
      campaignId,
      formId: selected.formId,
      ...(proposalId ? { proposalId } : {}),
    });
    navigate(
      setup ? `/proposals/setup/${encodeURIComponent(campaignId)}?${query}` : `/form?${query}`
    );
  };
  return (
    <div className={styles.card}>
      {forms.length > 1 && (
        <label className={styles.chooser}>
          Choose a native draft
          <select
            aria-label="Choose a native draft"
            value={selected?.formId ?? ''}
            onChange={(event) => setFormId(event.target.value)}
          >
            <option value="">Select a draft</option>
            {forms.map((form) => (
              <option key={form.formId} value={form.formId}>
                {form.formId}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className={styles.actions}>
        <AgxButton
          id="dashboard-facts"
          text={proposalId ? 'Edit facts' : 'Continue proposal draft'}
          variant="hollow"
          disabled={!selected}
          onClick={() => open(false)}
        />
        <AgxButton
          id="dashboard-photos"
          text="Photos & copy"
          variant="hollow"
          disabled={!selected}
          onClick={() => open(true)}
        />
      </div>
    </div>
  );
};
